import React from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { useMy } from '@/hooks';
import { CustomScanHeadersForm } from '@/sections/settings/components/forms/CustomScanHeadersForm';
import { SettingEditableCard } from '@/sections/settings/components/SettingEditableCard';
import { useAuth } from '@/state/auth';
import { Setting } from '@/types';

interface CustomScanHeadersCardProps {
  email?: string;
  editable?: boolean;
}

export const CustomScanHeadersCard: React.FC<CustomScanHeadersCardProps> = ({
  email,
  editable = true,
}) => {
  const { isPraetorianUser } = useAuth();

  const { data: settings, status } = useMy(
    { resource: 'setting' },
    {
      email,
      staleTime: 30000,
      gcTime: 300000,
    }
  );

  const customHeaders = settings?.find(
    (s: Setting) => s.name === 'custom_headers'
  )?.value as Record<string, string> | undefined;

  const headerCount = customHeaders ? Object.keys(customHeaders).length : 0;
  const isLoading = status === 'pending';

  return (
    <SettingEditableCard
      data-testid="custom-scan-headers-card"
      editConfig={{
        modalTitle: 'Custom Scan Headers',
        formComponent: CustomScanHeadersForm,
        formProps: {
          customerEmail: email,
          initialHeaders: customHeaders ?? {},
        },
        strategy: 'local-state',
        modalSize: 'xl',
        modalClassName: 'px-6 py-5',
        hideFooter: true, // Form has its own Save/Cancel
      }}
      editable={editable && isPraetorianUser}
      lockedTooltip={
        editable && !isPraetorianUser ? 'Managed by Praetorian' : undefined
      }
    >
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <h4 className="font-bold">Custom Scan Headers</h4>
        </div>
        <span className="text-chariot-text-secondary text-xs">
          {isLoading ? (
            <Skeleton className="h-3 w-40" />
          ) : headerCount > 0 ? (
            `${headerCount} custom header${headerCount !== 1 ? 's' : ''} configured`
          ) : (
            'No custom headers configured'
          )}
        </span>
      </div>
    </SettingEditableCard>
  );
};
